/**
 * 地点的按 id 回填和服务器搜索。选择器浏览走地点目录（placeDirectory.ts）。
 */
import { listingsApi } from '@/api'
import type { ListingKind } from '@/types/listing'

export type PlaceRow = {
  id: string
  nickname?: string | null
  listingKind?: ListingKind
  fullAddress?: string | null
  groupId?: string | null
  isActive?: boolean
}

export interface PlaceQuery {
  kinds: readonly ListingKind[]
  pickupSource?: boolean
}

const PLACE_FIELDS = 'id nickname listingKind fullAddress groupId isActive'

const scopeParams = ({ kinds, pickupSource }: PlaceQuery) => ({
  kind: [...kinds],
  ...(pickupSource ? { pickupSource: true } : {}),
})

const rowsOf = (res: any): PlaceRow[] => {
  const raw = res?.results ?? res?.items ?? res?.data ?? res
  return Array.isArray(raw) ? raw : []
}

const totalOf = (res: any, fallback: number): number =>
  res?.pagination?.totalResults ?? res?.total ?? fallback

/** 关键字搜索：服务端分页，跨传入的全部 kind。地点目录被截断时选择器用它 */
export async function searchPlaceRows(
  query: PlaceQuery & { q: string; page: number; pageSize: number },
): Promise<{ items: PlaceRow[]; total: number }> {
  if (query.kinds.length === 0) return { items: [], total: 0 }
  const res = await listingsApi.searchListing({
    q: query.q,
    columns: PLACE_FIELDS,
    orderBy: 'nickname',
    page: query.page,
    pageSize: query.pageSize,
    ...scopeParams(query),
  })
  const items = rowsOf(res)
  return { items, total: totalOf(res, items.length) }
}

interface PendingIds {
  ids: string[]
  resolve: (rows: PlaceRow[]) => void
  reject: (error: unknown) => void
}

let pendingIds: PendingIds[] = []

const ID_BATCH_SIZE = 1000

async function flushPlacesByIds() {
  const batch = pendingIds
  pendingIds = []
  // 后端只有全是数字 id 才跳过默认值。一个坏值不能让同批别的地点按默认值漏掉
  const ids = [...new Set(batch.flatMap((item) => item.ids))].filter((id) => /^\d+$/.test(id))
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += ID_BATCH_SIZE) chunks.push(ids.slice(i, i + ID_BATCH_SIZE))
  const pages = await Promise.allSettled(
    chunks.map((chunk) =>
      listingsApi.searchListing({
        filters: { id: { type: 'string', operator: 'in', value: chunk } },
        columns: PLACE_FIELDS,
        page: 1,
        pageSize: chunk.length,
      }),
    ),
  )
  const rows = pages.flatMap((page) => (page.status === 'fulfilled' ? rowsOf(page.value) : []))
  const missed = new Set(chunks.filter((_, i) => pages[i].status === 'rejected').flat())
  const failure = pages.find((page) => page.status === 'rejected')
  // 一批失败只影响要这批 id 的调用方
  batch.forEach((item) => {
    if (item.ids.some((id) => missed.has(id))) item.reject((failure as PromiseRejectedResult).reason)
    else item.resolve(rows.filter((row) => item.ids.includes(String(row.id))))
  })
}

/**
 * 按 id 回填标签。按 id 查时后端不加默认值，停用行和所有 kind 都能取到。
 * 同一轮渲染的调用合成一个请求：表格里每行一个选择器时，不能一行发一次。
 */
export function fetchPlacesByIds(ids: string[]): Promise<PlaceRow[]> {
  if (ids.length === 0) return Promise.resolve([])
  return new Promise((resolve, reject) => {
    if (pendingIds.length === 0) queueMicrotask(() => void flushPlacesByIds())
    pendingIds.push({ ids: ids.map(String), resolve, reject })
  })
}

/** 没有 nickname 时的显示名。不显示完整 Snowflake id */
export const placeName = (row: { id: string; nickname?: string | null; title?: string | null; fullAddress?: string | null }): string =>
  row.nickname || row.title || row.fullAddress || `#${String(row.id).slice(-6)}`
